<template>
  <div class="devices content-block">
    <div class="options">
      <dx-button type="normal"
        :text="$t('general.resetFilter')"
        styling-mode="contained"
        @click="onResetState"
      />

      <active-button
        class="standart-btn"
        type="normal"
        :text="$t('general.exportToExcel')"
        :hint="$t('general.exportToExcel')"
        :disabled="excelExport"
        :busy="excelExport"
        @click="onExportToExcel"
      />

      <active-button v-if="canDowloadWalkFile"
        icon="download"
        type="normal"
        class="standart-btn"
        :style="{padding: '0px 10px'}"
        :text="$t('devices.actions.downloadUnknown')"
        :hint="$t('devices.hints.downloadUnknown')"
        :disabled="disableDownloadWalkFileButton"
        :busy="disableDownloadWalkFileButton"
        @click="onDownloadWalkFiles"
      />

      <div class="status">
        <aims2-button
          type="default"
          :text="$t('devices.actions.enableMaintenance')"
          :disabled="disableMaintenance"
          :busy="actionEnable"
          @click="enableMaintenanceDevices"
        />
        <aims2-button
          type="default"
          :text="$t('devices.actions.disableMaintenance')"
          :disabled="disableMaintenance"
          :busy="actionDisable"
          @click="disableMaintenanceDevices"
        />
      </div>
    </div>

    <dx-data-grid
      class="data-grid dx-card"
      :data-source="dataSourceConfig"
      :remote-operations="remoteOperations"
      :syncLookupFilterValues="false"
      :height="gridHeight"
      @content-ready="onContentReady"
      @option-changed="onOptionChanged"
      @row-click="onRowClick"
      @row-prepared="onRowPrepared"
      @editor-preparing="onEditorPreparing"
      @context-menu-preparing="addMenuItems"
      @initialized=initializedGrid
      ref="dataGrid"
    >
      <dx-paging :enabled="true" />
      <dx-pager
        :visible="true"
        :allowed-page-sizes="pageSize"
        display-mode="full"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="false"
      />
      <DxSelection
        mode="multiple"
        select-all-mode="page"
      />

      <dx-filter-row :visible="!hideFilterRow" />
      <dx-header-filter :visible="true" />
      <dx-sorting mode="multiple" />
      <dx-state-storing
        :enabled="true"
        type="custom"
        :customLoad="stateLoad"
        :customSave="stateSave"
      />
      <dx-filter-panel :visible="showFilterPanel" />

      <dx-column
        data-field="device.status.id"
        :caption="$t('devices.statusCode')"
        :visible-index="0"
        :width="100"
        data-type="string"
        :allow-header-filtering="true"
      >
        <dx-lookup
          :data-source="printerStatusCodes"
          value-expr="id"
          display-expr="name"
        />
      </dx-column>

      <dx-column
        data-field="device.isActive"
        :caption="$t('device.detail.managed')"
        :visible-index="1"
        :width="150"
        data-type="boolean"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="device.modelCode"
        :caption="$t('devices.model')"
        :visible-index="2"
        :width="260"
        data-type="string"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="device.serialNumber"
        :caption="$t('devices.sn')"
        :visible-index="3"
        :width="140"
        data-type="string"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="device.inventoryNumber"
        :caption="$t('devices.inventoryNumber')"
        :visible-index="4"
        :width="120"
        data-type="string"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="lixLocation"
        :caption="$t('devices.location')"
        :visible-index="5"
        :width="80"
        data-type="string"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="pixHostname"
        :caption="$t('devices.host')"
        :visible-index="6"
        :width="120"
        data-type="string"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="device.location.name"
        :caption="$t('devices.group')"
        :visible-index="7"
        :width="160"
        data-type="string"
        :allow-header-filtering="true"
      >
        <dx-header-filter :data-source="locationHeaderFilter" />
      </dx-column>

      <dx-column
        data-field="pixIp"
        :caption="$t('devices.ip')"
        :visible-index="8"
        :width="125"
        data-type="string"
        :allow-header-filtering="false"
      />

      <dx-column
        data-field="device.model.dim01.id"
        :caption="$t('devices.type')"
        :visible-index="9"
        :width="80"
        data-type="number"
        :allow-header-filtering="true"
      >
        <dx-lookup
          :data-source="printerTypes"
          value-expr="id"
          display-expr="name"
        />
      </dx-column>

      <dx-column
        data-field="device.model.dim02.id"
        :caption="$t('devices.colorful')"
        :visible-index="10"
        :width="90"
        data-type="string"
        :allow-header-filtering="true"
      >
        <dx-lookup
          :data-source="colorTypes"
          value-expr="id"
          display-expr="name"
        />
      </dx-column>

      <dx-column
        data-field="device.model.dim03.id"
        :caption="$t('devices.paper')"
        :visible-index="11"
        :width="80"
        data-type="string"
        :allow-header-filtering="true"
      >
        <dx-lookup
          :data-source="paperFormats"
          value-expr="id"
          display-expr="name"
        />
      </dx-column>

      <dx-column
        data-field="lixPrintedPageCount"
        :caption="$t('devices.printed')"
        :visible-index="12"
        :width="80"
        :allow-header-filtering="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />

      <dx-column
        data-field="lixBlackTonerLevel"
        :caption="$t('devices.black')"
        :visible-index="13"
        :width="100"
        :allow-header-filtering="false"
        data-type="number"
        cell-template="black-toner-cell"
      />

      <dx-column
        data-field="lixCyanTonerLevel"
        :caption="$t('devices.cyan')"
        :visible-index="14"
        :width="100"
        :allow-header-filtering="false"
        data-type="number"
        cell-template="cyan-toner-cell"
      />

      <dx-column
        data-field="lixMagentaTonerLevel"
        :caption="$t('devices.magenta')"
        :visible-index="15"
        :width="100"
        :allow-header-filtering="false"
        data-type="number"
        cell-template="magenta-toner-cell"
      />

      <dx-column
        data-field="lixYellowTonerLevel"
        :caption="$t('devices.yellow')"
        :visible-index="16"
        :width="100"
        :allow-header-filtering="false"
        data-type="number"
        cell-template="yellow-toner-cell"
      />

      <template #black-toner-cell="{ data }">
        <div v-if="typeof data.displayValue === 'number'" class="progress-cell">
          <DxProgressBar
              :class="getClassProgress(data.value)"
              :min="0"
              :max="100"
              :value="data.value <= 4 ? 4 : data.value"
              :showStatus="false"
          />
          <div class="level">{{ data.value + '%' }}</div>
        </div>
      </template>

      <template #cyan-toner-cell="{ data }">
        <div v-if="typeof data.displayValue === 'number'" class="progress-cell">
          <DxProgressBar
              :class="getClassProgress(data.value)"
              :min="0"
              :max="100"
              :value="data.value <= 4 ? 4 : data.value"
              :showStatus="false"
          />
          <div class="level">{{ data.value + '%' }}</div>
        </div>
      </template>

      <template #magenta-toner-cell="{ data }">
        <div v-if="typeof data.displayValue === 'number'" class="progress-cell">
          <DxProgressBar
              :class="getClassProgress(data.value)"
              :min="0"
              :max="100"
              :value="data.value <= 4 ? 4 : data.value"
              :showStatus="false"
          />
          <div class="level">{{ data.value + '%' }}</div>
        </div>
      </template>

      <template #yellow-toner-cell="{ data }">
        <div v-if="typeof data.displayValue === 'number'" class="progress-cell">
          <DxProgressBar
              :class="getClassProgress(data.value)"
              :min="0"
              :max="100"
              :value="data.value <= 4 ? 4 : data.value"
              :showStatus="false"
          />
          <div class="level">{{ data.value + '%' }}</div>
        </div>
      </template>

      <dx-column
        data-field="lixDeviceStatus"
        :caption="$t('devices.lixDeviceStatus')"
        :width="80"
        :allow-header-filtering="false"
        data-type="string"
        :visible="false"
      />

      <dx-column
        data-field="factDate"
        :caption="$t('devices.factDate')"
        :width="146"
        :allow-header-filtering="false"
        :visible="false"
        data-type="date"
        format="dd.MM.yyyy"
      />
      <dx-column
        data-field="pixFirmwareVersion"
        :caption="$t('devices.pixFirmwareVersion')"
        :width="80"
        :allow-header-filtering="false"
        data-type="string"
        :visible="false"
      />
      <dx-column
        data-field="pixPort"
        :caption="$t('devices.pixPort')"
        :width="50"
        :allow-header-filtering="false"
        data-type="number"
        :visible="false"
      />
      <dx-column
        data-field="lixMac"
        :caption="$t('devices.lixMac')"
        :width="130"
        :allow-header-filtering="false"
        data-type="string"
        :visible="false"
      />
      <dx-column
        data-field="pixPrinterStatus"
        :caption="$t('devices.pixPrinterStatus')"
        :width="200"
        data-type="string"
        :visible="false"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="pixScannerStatus"
        :caption="$t('devices.pixScannerStatus')"
        :width="200"
        data-type="string"
        :visible="false"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="pixDeviceStatusCode"
        :caption="$t('devices.pixDeviceStatusCode')"
        :width="120"
        :allow-header-filtering="false"
        data-type="number"
        :visible="false"
      />
      <dx-column
        data-field="lixPrintedBwPageCount"
        :caption="$t('devices.lixPrintedBwPageCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="lixPrintedColorPageCount"
        :caption="$t('devices.lixPrintedColorPageCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="lixCopyPageCount"
        :caption="$t('devices.lixCopyPageCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="lixScanPageCount"
        :caption="$t('devices.lixScanPageCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="newDevice30Flag"
        :caption="$t('devices.newDevice30Flag')"
        :width="80"
        :allow-header-filtering="false"
        data-type="boolean"
        :visible="false"
      />
      <dx-column
        data-field="notAvailableFlag"
        :caption="$t('devices.notAvailableFlag')"
        :width="80"
        :allow-header-filtering="false"
        data-type="boolean"
        :visible="false"
      />
      <dx-column
        data-field="matReplacementRequiredFlag"
        :caption="$t('devices.matReplacementRequiredFlag')"
        :width="100"
        :allow-header-filtering="false"
        data-type="boolean"
        :visible="false"
      />
      <dx-column
        data-field="needAttentionFlag"
        :caption="$t('devices.needAttentionFlag')"
        :width="80"
        :allow-header-filtering="false"
        data-type="boolean"
        :visible="false"
      />
      <dx-column
        data-field="dayPrintedBwPageCount"
        :caption="$t('devices.dayBwPageCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="dayPrintedColorPageCount"
        :caption="$t('devices.dayColorPageCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <!-- NIC165-34 Новые показатели -->
      <dx-column
        data-field="lixPrintedPageA3Count"
        :caption="$t('devices.lixPrintedPageA3Count')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="lixPrintedPageA3BwCount"
        :caption="$t('devices.lixPrintedPageA3BwCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="lixPrintedPageA3ColorCount"
        :caption="$t('devices.lixPrintedPageA3ColorCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="lixPrintedPageA4Count"
        :caption="$t('devices.lixPrintedPageA4Count')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="lixPrintedPageA4BwCount"
        :caption="$t('devices.lixPrintedPageA4BwCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="lixPrintedPageA4ColorCount"
        :caption="$t('devices.lixPrintedPageA4ColorCount')"
        :width="80"
        :allow-header-filtering="false"
        :visible="false"
        data-type="number"
        :format="{ formatter: (val) => formatNumber(val, '#,##0') }"
      />
      <dx-column
        data-field="device.model.vendor.code"
        :caption="$t('devices.modelVendorCode')"
        :width="100"
        :allow-header-filtering="false"
        :visible="false"
        data-type="string"
      />

      <dx-column
        data-field="device.createDate"
        :caption="$t('device.detail.createDate')"
        :width="160"
        :allow-header-filtering="false"
        :visible="true"
        data-type="datetime"
        format="dd.MM.yyyy HH:mm:ss"
      />
      <dx-column
        data-field="device.refreshDate"
        :caption="$t('device.detail.refreshDate')"
        :width="160"
        :allow-header-filtering="false"
        :visible="false"
        data-type="date"
        format="dd.MM.yyyy HH:mm:ss"
      />
      <dx-column
        data-field="device.walkRefreshDate"
        :caption="$t('device.detail.walkRefreshDate')"
        :width="160"
        :allow-header-filtering="false"
        :visible="false"
        data-type="date"
        format="dd.MM.yyyy HH:mm:ss"
      />
    </dx-data-grid>

    <CustomColumnChooser
      :visible="visible"
      :columns="columns"
      :onApply="onApply"
      :onHiding="onHiding"
    />
  </div>
</template>

<script>
import { mapCacheActions } from "vuex-cache";

import baseView from "../base";
import appInfo from "../../app-info";

import DxDataGrid, {
  DxColumn, DxLookup, DxFilterRow, DxHeaderFilter, DxPaging,
  DxStateStoring, DxFilterPanel, DxPager, DxSorting, DxSelection
} from "devextreme-vue/data-grid";
import CustomStore from 'devextreme/data/custom_store';
import DxButton from "devextreme-vue/button";
import { formatNumber } from "devextreme/localization";
import { DxProgressBar } from 'devextreme-vue/progress-bar';

import { notifier } from '@/shared/lib/notifier/index';

import ActiveButton from '@/components/active-button.vue';
import aims2Button from '@/components/active-button.vue';
import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { getUserLocations, download, actualDeviceDays, postDevicesChangeActivity } from "@/services/api";
import { getFillStyle, getClassProgress } from '@/services/cell-consumables.js';
import { AclHandler } from "@/services/acl-handler";

import { DataDrid } from "@/mixins/dataDridMixin";
import { DataLocation } from "@/mixins/dataLocationMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

import moment from "moment";

export const dictDataSourceConfig = (data) => ({
  store: { data, type: "array", key: "id" },
  map: rec => ({ value: rec.id, ...rec })
});

const isEmptyArray = (arr) => Array.isArray(arr) && arr.length === 0;

export default {
  name: "devices",
  extends: baseView,
  props: ["hideFilterRow", "noBreadcrumbMenu"],
  mixins: [DataDrid, DataLocation],
  components: {
    DxDataGrid, DxColumn, DxLookup, DxFilterRow, DxHeaderFilter, DxPaging, DxStateStoring, DxFilterPanel,
    DxButton, ActiveButton, aims2Button, CustomColumnChooser, DxPager, DxSorting, DxProgressBar, DxSelection
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      remoteOperations: { groupPaging: true },
      dataSourceConfig: null,
      loadOptionsDevices: null,
      originalNameFilter: null,
      routeFilterAppliedToGrid: false,
      locationIdsByName: new Map(),
      locationNameById: new Map(),
      locationChildrenByParentId: new Map(),
      saveFilter: true,
      optionChangedGridFlag: false,
      locationHeaderFilter: [],
      printerTypes: null,
      colorTypes: null,
      paperFormats: null,
      printerStatusCodes: null,
      disableDownloadWalkFileButton: false,
      excelExport: false,
      disableMaintenance: true,
      actionEnable: false,
      actionDisable: false,
    };
  },
  computed: {
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    showFilterPanel() {
      return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
    },
    canDowloadWalkFile() {
      return this.aclUser.hasGrantForOperation('BTN_DOWNLOAD_WALK', 'EXECUTE') || false;
    },
    sumGap() {
      return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
    },
    gridHeight() {
      const appHeightSize = this.$store.getters.appContainerSize.height;
      return (appHeightSize || 800) - this.sumGap;
    },
    gridBodyHeight() {
      return this.gridHeight - GRID_HEADER_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0) - GRID_FILTER_ROW_HEIGHT - GRID_PAGER_HEIGHT;
    },
  },
  methods: {
    ...mapCacheActions(["getPrinterTypes", "getColorTypes", "getPaperFormats", "getPrinterStatusCodes"]),
    getFillStyle, getClassProgress,
    //
    // нормализатор фильтра перед API (location.name в location.id)
    // строковые операции вроде contains оставляет по name
    //
    transformLocationNameToId(filter) {
      if (!filter) return null;
      if (!Array.isArray(filter)) return filter;

      const locationOrConditions = this.getLocationEqualsOrConditions(filter);
      if (locationOrConditions) {
        const idsByCondition = locationOrConditions
          .map(condition => this.getLocationIdsByFilterValue(condition[2]));

        if (idsByCondition.every(ids => ids.length && ids.every(id => id !== null))) {
          const ids = Array.from(new Set(idsByCondition.reduce((result, ids) => result.concat(ids), [])));
          return ["device.location.id", "anyof", ids];
        }

        return filter.map(item => Array.isArray(item) ? this.transformLocationNameToId(item) : item);
      }

      if (this.isLocationFilterCondition(filter)) {
        const [field, operator, value] = filter;

        if (["anyof", "noneof"].includes(operator)) {
          if (!Array.isArray(value)) return filter;

          const ids = value
            .reduce((result, item) => result.concat(this.getLocationIdsByFilterValue(item)), []);

          return ["device.location.id", operator, Array.from(new Set(ids))];
        }

        if (["=", "<>"].includes(operator)) {
          const ids = this.getLocationIdsByFilterValue(value);
          if (!ids.length) return filter;
          if (ids.length === 1) return ["device.location.id", operator, ids[0]];

          return ["device.location.id", operator === "=" ? "anyof" : "noneof", ids];
        }

        return [field, operator, value];
      }

      if (Array.isArray(filter[0])) {
        return filter.map(item => Array.isArray(item) ? this.transformLocationNameToId(item) : item);
      }

      return filter;
    },
    //
    // проверяет, что массив является простым условием по локации формата [field, operator, value]
    //
    isLocationFilterCondition(filter) {
      return Array.isArray(filter) &&
        filter.length === 3 &&
        ["device.location.name", "device.location.id"].includes(filter[0]) &&
        typeof filter[1] === "string";
    },
    //
    // преобразует значение фильтра в массив id локаций
    //
    getLocationIdsByFilterValue(value) {
      if (value === null) return [null];
      if (value === undefined || value === "") return [];
      if (typeof value === "number") return [value];

      if (this.locationIdsByName.has(value)) return this.locationIdsByName.get(value);

      return [];
    },
    //
    // проверяет, является ли фильтр чистой OR-цепочкой равенств по локации,
    // чтобы потом схлопнуть их в один anyof по id
    //
    getLocationEqualsOrConditions(filter) {
      if (!Array.isArray(filter) || !filter.includes("or")) return null;

      const conditions = [];
      const walk = (item) => {
        if (!Array.isArray(item)) return item === "or";

        if (this.isLocationFilterCondition(item)) {
          const [field, operator, value] = item;
          const isSupportedValue = value === null || value === undefined || value === "" ||
            typeof value === "number" ||
            this.getLocationIdsByFilterValue(value).length > 0;

          if (["device.location.name", "device.location.id"].includes(field) && operator === "=" && isSupportedValue) {
            conditions.push(item);
            return true;
          }
        }

        const nestedConditions = this.getLocationEqualsOrConditions(item);
        if (nestedConditions) {
          conditions.push(...nestedConditions);
          return true;
        }

        return false;
      };

      for (let i = 0; i < filter.length; i++) {
        const item = filter[i];
        if (i % 2 === 1) {
          if (item !== "or") return null;
        } else if (!walk(item)) {
          return null;
        }
      }

      return conditions.length ? conditions : null;
    },
    //
    // возвращает id выбранной локации и всех её дочерних локаций (используется при переходе из карточки location)
    //
    getChildrenRecursive(id) {
      const result = [];
      const stack = [id];

      while (stack.length) {
        const currentId = stack.pop();
        result.push(currentId);
        const children = this.locationChildrenByParentId.get(currentId) || [];
        stack.push(...children.map(l => l.id));
      }

      return result;
    },
    onOptionChanged(e) {
      if (e.fullName === "filterValue") {
        if (this.optionChangedGridFlag) {
          this.saveFilter = true;
        }
      }
      if (e.fullName === "selectedRowKeys") {
        this.disableMaintenance = e.value.length === 0;
      }
    },
    onEditorPreparing(e) {
      this.applyFilterRowTimeout(e);
    },
    onContentReady(e) {
      this.addFitToPageHeightButton(e);
    },
    formatNumber(val, format) {
      const text = formatNumber(val, format);
      return text? text.replace(/,/g, "\xA0"): "";
    },
    stateLoad() {
      // Берём высоту таблицы из вычисляемого свойства
      const height = this.gridBodyHeight;
      let state = this.$store.getters.gridDevices;

      if (!state)
        state = {
          pageIndex: 0,
          pageSize: this.countRows(height)
        };

      state = this.normalizeGridState(state);

      state.selectedRowKeys = [];
      state.pageSize = state.pageSize === 0 ? this.countRows(height) : state.pageSize;

      return state;
    },
    stateSave(state) {
      if (this.saveFilter)
        return this.$store.commit('setGridDevices', state);
    },
    async enableMaintenanceDevices() {
      try {
        this.actionEnable = true;
        this.disableMaintenance = true;

        const selectedRows = this.dataGrid.getSelectedRowsData();
        const devicesIds = selectedRows.map(i => i.device.id);

        const response = await postDevicesChangeActivity(true, devicesIds);
        if (response.status === 'SUCCESS')
          notifier.showSuccess(response.message);
        else
          notifier.showError(response.message);
      } catch (error) {
        this.onDataError(error);
      } finally {
        await this.dataGrid.clearSelection();
        await this.dataGrid.refresh();
        this.actionEnable = false;
      }
    },
    async disableMaintenanceDevices() {
      try {
        this.actionDisable = true;
        this.disableMaintenance = true;

        const selectedRows = this.dataGrid.getSelectedRowsData();
        const devicesIds = selectedRows.map(i => i.device.id);

        const response = await postDevicesChangeActivity(false, devicesIds);
        if (response.status === 'SUCCESS')
          notifier.showSuccess(response.message);
        else
          notifier.showError(response.message);
      } catch (error) {
        this.onDataError(error);
      } finally {
        await this.dataGrid.clearSelection();
        await this.dataGrid.refresh();
        this.actionDisable = false;
      }
    },
    onRowClick(e) {
      if (window.getSelection().focusOffset !== window.getSelection().anchorOffset)
        return;

      if (e.event.metaKey || e.event.ctrlKey) {
        const routeData = this.$router.resolve({
          name: "device",
          params: { id: e.data.device.id }
        });
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push({
          name: "device",
          params: { id: e.data.device.id }
        });
      }
    },
    onRowPrepared(e) {
      if (e.rowType === "data") {
        switch (e.data.device.status && e.data.device.status.code) {
          case 'OK':
            e.rowElement.style.color = 'rgb(0,128,0)';
            return;
          case 'WARNING':
          case 'SCAN_ERROR':
            e.rowElement.style.color = 'rgb(255,165,0)';
            return;
          case 'ERROR':
          case 'NOT_AVAILABLE':
            e.rowElement.style.color = 'rgb(255,0,0)';
            return;
          case 'NOT_SERVICED':
            e.rowElement.style.color = 'rgb(170,170,170)';
            return;
        
          default:
            return;
        }
      }
    },
    onResetState() {
      this.dataGrid.clearFilter();
      this.originalNameFilter = null;
    },
    async onExportToExcel() {
      this.excelExport = true;
      const params = new URLSearchParams();

      if (this.loadOptionsDevices.sort.length > 0) {
        params.append('sort', this.loadOptionsDevices.sort);
      }

      if (this.loadOptionsDevices.filterJson) {
        params.append('filter-json', JSON.stringify(this.loadOptionsDevices.filterJson));
      }

      download(`/dm-device-days-actual-v/export?${params}`).then(() => {
        this.excelExport = false;
        notifier.showSuccess(this.$t('general.uploadSuccess'));
      }).catch((err) => {
        this.onDataError(err);
        this.excelExport = false;
      })
    },
    async onDownloadWalkFiles() {
      this.disableDownloadWalkFileButton = true;
      // http://cronus.nicotech.ru:8888/issue/NIC165-21#focus=Comments-73-35339.0-0
      // eslint-disable-next-line no-console
      // console.debug("Devices::onDownloadWalkFiles");
      try {
        // Refused to get unsafe header "Content-Disposition
        await download(`/dm-device-days-v/device/all/walk/unknown_devices.zip`, "unknown_devices.zip");
        notifier.showSuccess(this.$t('general.uploadSuccess'));
      } catch (err) {
        this.onDataError(err);
      }
      this.disableDownloadWalkFileButton = false;
    },

    beforeQueryData({ filterJson }) {
      function formatFactDate(query) {
        if (Array.isArray(query)) {
          if (query[0] === "factDate") {
            if (query[2] instanceof Date) {
              query[2] = moment(query[2]).format("YYYY-MM-DD");
            }
            return;
          }
          for (let clause of query) {
            formatFactDate(clause);
          }
        }
      }
      formatFactDate(filterJson);
      // eslint-disable-next-line no-console
      // console.debug("filterJson", filterJson);
    },
    filterOutDate(filter) {
      const filterOutDate = (filter) => {
        if (Array.isArray(filter)) {
          if (filter[0] === "factDate") {
            return [];
          }
          const result = [];
          for (const part of filter) {
            result.push(filterOutDate(part));
          }
          for (let i = 0; i < result.length; i++) {
            if (isEmptyArray(result[i])) {
              if (i > 0 && ["and", "or"].includes(result[i - 1])) {
                result.splice(i - 1, 2);
              } else if (i < result.length - 1 && ["and", "or"].includes(result[i + 1])) {
                result.splice(i, 2);
              }
            }
          }
          return result.length === 1? result[0]: result;
        } else {
          return filter;
        }
      }
      return filterOutDate(filter);
    },
  },
  async created() {
    if (!this.noBreadcrumbMenu) {
      document.title = `${appInfo.title} - ${this.$t("devices.title")}`;
      this.$store.commit("setBreadcrumb", [{
        link: "/devices",
        title: this.$t("devices.title")
      }]);
    }

    // Загружаем локации
    this.locations = await getUserLocations();
    this.locationIdsByName = this.locations.reduce((map, loc) => {
      const ids = map.get(loc.name) || [];
      ids.push(loc.id);
      map.set(loc.name, ids);

      return map;
    }, new Map());
    this.locationNameById = new Map(this.locations.map(loc => [loc.id, loc.name]));
    this.locationChildrenByParentId = this.locations.reduce((map, loc) => {
      if (loc.parent && loc.parent.id) {
        const children = map.get(loc.parent.id) || [];
        children.push(loc);
        map.set(loc.parent.id, children);
      }

      return map;
    }, new Map());

    this.locationHeaderFilter = this.locations.map(loc => ({
      text: loc.name,
      value: loc.name
    }));

    // Составляем фильтр, который приходит с карточки location detail
    const params = this.$route.params;
    if (params.filter) {
      this.originalNameFilter = params.filter;
    }

    if (params.locationId) {
      const id = Number(params.locationId);
      const childIds = this.getChildrenRecursive(id);
      const locationNames = childIds
        .map(childId => this.locationNameById.get(childId))
        .filter(name => name !== undefined);

      this.originalNameFilter = ["device.location.name", "anyof", locationNames]; // фильтр для data-grid

      if (params.deviceStatusId) {
        const statusFilter = ["device.status.id", "=", Number(params.deviceStatusId)];
        this.originalNameFilter = [this.originalNameFilter, "and", statusFilter];
      }
    }

    // Настраиваем dataSource только после готовности фильтра
    this.dataSourceConfig = new CustomStore({
      key: "id",
      load: async (options) => {
        // Берём высоту таблицы из вычисляемого свойства
        const height =  this.gridBodyHeight;

        const take = options.take || this.countRows(height); // this.countRows(height) или 20
        const skip = options.skip || 0;
        const pageNumber = Math.floor(skip / take) + 1;
        const page = [take, pageNumber].join(",");

        const sort = Array.isArray(options.sort)
          ? options.sort.map(s => `${s.selector},${s.desc ? "DESC" : "ASC"}`)
          : [];

        const rawFilter = options.filter !== undefined
          ? options.filter
          : (this.routeFilterAppliedToGrid ? null : this.originalNameFilter);
        const filterJson = this.transformLocationNameToId(rawFilter);

        const query = { page, sort, filterJson };

        this.loadOptionsDevices = { sort, filterJson };
        try {
          const data = await actualDeviceDays(query);
          return {
            data: data.pageRows,
            totalCount: data.totalRows
          };
        } catch (err) {
          this.onDataError(err);
        }
      }
    });

    this.$nextTick(() => {
      if (this.dataGrid && this.originalNameFilter) {
        const state = this.dataGrid.state();
        state.filterValue = this.originalNameFilter;
        this.dataGrid.state(state);

        this.routeFilterAppliedToGrid = true;
        this.optionChangedGridFlag = true; // разрешим реагировать на изменения позже
        this.saveFilter = false;           // не сохраняем установленный из params фильтр
      } else {
        this.saveFilter = true; // нет начального фильтра -> пользовательский
      }
    });

    this.printerTypes = dictDataSourceConfig(await this.getPrinterTypes());
    this.colorTypes = dictDataSourceConfig(await this.getColorTypes());
    this.paperFormats = dictDataSourceConfig(await this.getPaperFormats());
    this.printerStatusCodes = dictDataSourceConfig(await this.getPrinterStatusCodes());
  },
  mounted() { },
  beforeDestroy() {
    const state = this.dataGrid.state();
    this.stateSave(state);

    this.dataGrid.clearSelection();
  },
  watch: {}
};
</script>

<style lang="scss">
.devices {
  .options {
    display: flex;
    flex-direction: row;
    gap: 8px;
    margin-top: 10px;

    .status {
      margin-left: auto;
      display: flex;
      flex-direction: row;
      gap: 8px;
    }

    .standart-btn {
      .dx-loadindicator-segment {
        background: #337ab7 !important;
      }
    }
  }

  .dx-datagrid {
    .dx-toolbar {
      .dx-toolbar-item {
        padding: 5px;
      }
    }
  }
}
</style>
